import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { 
  Scale, Send, Plus, MessageSquare, Trash2, LogOut, 
  FileText, Menu, X, Loader2, User, Bot, ChevronRight, Paperclip, FileUp, Search, Clock, Shield, ShoppingBag, Zap
} from 'lucide-react';
import { ModuleFlyout, ModuleTriggerButton } from '../components/ModuleFlyout';
import { PrivatFlyout, PrivatTriggerButton } from '../components/PrivatFlyout';
import { usePageSEO } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatPage() {
  const { user, token, logout } = useAuth();
  
  // Apply SEO meta tags
  usePageSEO('chat');
  
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false); // Default closed on mobile
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadedPdf, setUploadedPdf] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [personalizedGreeting, setPersonalizedGreeting] = useState(null);
  const [moduleFlyoutOpen, setModuleFlyoutOpen] = useState(false);
  const [privatFlyoutOpen, setPrivatFlyoutOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  // Check if desktop to auto-open sidebar
  useEffect(() => {
    const checkDesktop = () => {
      if (window.innerWidth > 768) {
        setSidebarOpen(true);
      }
    };
    checkDesktop();
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  const loadConversations = useCallback(async (query = '') => {
    try {
      const endpoint = query ? `${API}/conversations/search?q=${encodeURIComponent(query)}` : `${API}/conversations`;
      const res = await axios.get(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      setConversations(res.data);
    } catch (err) {
      // Silent fail for conversations
    }
    setLoadingConvs(false);
  }, [token]);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // Load personalized greeting
  useEffect(() => {
    const loadGreeting = async () => {
      try {
        const res = await axios.get(`${API}/personalization/greeting`, { headers: { Authorization: `Bearer ${token}` } });
        setPersonalizedGreeting(res.data);
      } catch (err) {
        // Silent fail - use default
      }
    };
    loadGreeting();
  }, [token]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        loadConversations(searchQuery);
      } else {
        loadConversations();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, loadConversations]);

  const loadMessages = async (convId) => {
    try {
      const res = await axios.get(`${API}/conversations/${convId}`, { headers });
      setMessages(res.data.messages);
      setActiveConv(res.data.conversation);
    } catch {
      // Silent fail - UI handles missing data
    }
  };

  const createConversation = async () => {
    try {
      const res = await axios.post(`${API}/conversations`, { title: 'Ny samtale' }, { headers });
      await loadConversations();
      setActiveConv(res.data);
      setMessages([]);
      inputRef.current?.focus();
    } catch {
      // Silent fail
    }
  };

  const deleteConversation = async (convId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/conversations/${convId}`, { headers });
      if (activeConv?.id === convId) {
        setActiveConv(null);
        setMessages([]);
      }
      await loadConversations();
    } catch {
      // Silent fail
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;

    if (!activeConv) {
      try {
        const res = await axios.post(`${API}/conversations`, { title: 'Ny samtale' }, { headers });
        setActiveConv(res.data);
        await sendWithConv(res.data.id);
        await loadConversations();
      } catch {
        // Silent fail
      }
      return;
    }

    await sendWithConv(activeConv.id);
  };

  const sendWithConv = async (convId) => {
    const content = input.trim();
    let finalContent = content;

    // If a PDF was uploaded, include its text in the message
    if (uploadedPdf) {
      finalContent = `[Opplastet PDF: ${uploadedPdf.filename}, ${uploadedPdf.page_count} sider]\n\nInnhold fra dokumentet:\n${uploadedPdf.extracted_text?.substring(0, 15000) || ''}\n\nBrukerens spørsmål: ${content}`;
      setUploadedPdf(null);
    }

    setInput('');
    setSending(true);

    // Optimistic user message (will be replaced by real one from stream)
    const tempUserMsg = { id: 'temp-user', role: 'user', content, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, tempUserMsg]);

    // Add placeholder for streaming assistant message
    const tempAssistantMsg = { id: 'temp-assistant', role: 'assistant', content: '', created_at: new Date().toISOString(), isStreaming: true };
    setMessages(prev => [...prev, tempAssistantMsg]);

    try {
      const response = await fetch(`${API}/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: finalContent,
          conversation_id: convId
        })
      });

      if (!response.ok) {
        throw new Error('Streaming request failed');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let streamedContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'user_message') {
                // Replace temp user message with real one
                setMessages(prev => prev.map(m => 
                  m.id === 'temp-user' ? data.message : m
                ));
              } else if (data.type === 'content') {
                // Append content to streaming message
                streamedContent += data.content;
                setMessages(prev => prev.map(m => 
                  m.id === 'temp-assistant' ? { ...m, content: streamedContent } : m
                ));
              } else if (data.type === 'done') {
                // Replace temp assistant message with final one
                setMessages(prev => prev.map(m => 
                  m.id === 'temp-assistant' ? { ...data.message, isStreaming: false } : m
                ));
              } else if (data.type === 'error') {
                setMessages(prev => prev.filter(m => m.id !== 'temp-assistant'));
              }
            } catch (e) {
              // Ignore parse errors for incomplete chunks
            }
          }
        }
      }

      await loadConversations();
    } catch {
      setMessages(prev => prev.filter(m => m.id !== 'temp-user' && m.id !== 'temp-assistant'));
      setInput(content);
    }
    setSending(false);
    inputRef.current?.focus();
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      alert('Kun PDF-filer er støttet');
      return;
    }
    setUploadingPdf(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/pdf/upload`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setUploadedPdf(res.data);
      setInput(`Jeg har lastet opp dokumentet "${res.data.filename}" (${res.data.page_count} sider). Analyser innholdet og identifiser viktige juridiske punkter.`);
      inputRef.current?.focus();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Feil ved opplasting av PDF';
      alert(detail);
    }
    setUploadingPdf(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="h-screen flex bg-[#0B1120] noise-overlay" data-testid="chat-page">
      {/* Module Flyout - rendered at root level for proper positioning */}
      <ModuleFlyout 
        isOpen={moduleFlyoutOpen} 
        onClose={() => setModuleFlyoutOpen(false)} 
      />
      
      {/* Privat Flyout - rendered at root level for proper positioning */}
      <PrivatFlyout 
        isOpen={privatFlyoutOpen} 
        onClose={() => setPrivatFlyoutOpen(false)} 
      />
      
      {/* Mobile Menu Button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="mobile-menu-btn fixed top-3 left-3 z-50 p-2 bg-[#131C2E] border border-white/10 rounded-lg md:hidden"
        data-testid="mobile-menu-toggle"
      >
        {sidebarOpen ? <X className="w-5 h-5 text-[#F0F4F8]" /> : <Menu className="w-5 h-5 text-[#F0F4F8]" />}
      </button>

      {/* Sidebar */}
      <div className={`chat-sidebar ${sidebarOpen ? 'w-72 mobile-open' : 'w-0'} transition-all duration-300 bg-[#131C2E]/95 backdrop-blur-md border-r border-white/5 flex flex-col overflow-hidden flex-shrink-0`}
           data-testid="chat-sidebar">
        {/* Sidebar Header */}
        <div className="p-4 border-b border-white/5 mobile-header">
          <div className="flex items-center gap-2.5 mb-4">
            <img src="https://customer-assets.emergentagent.com/job_juridisk-motor-no/artifacts/1x4dyzgp_image.jpg" alt="RettVerk" className="w-8 h-8 rounded object-contain flex-shrink-0 logo" />
            <div className="min-w-0">
              <h1 className="text-sm font-bold tracking-tight truncate header-title" style={{ fontFamily: 'Manrope, sans-serif' }}>
                RettVerk 1.2.0
              </h1>
              <p className="text-[10px] text-[#8899A8] tracking-wider uppercase">Juridisk assistent</p>
            </div>
          </div>
          <button
            data-testid="new-conversation-button"
            onClick={() => { createConversation(); setSidebarOpen(false); }}
            className="w-full bg-[#C07850] text-white font-semibold py-2.5 rounded text-xs uppercase tracking-wider hover:bg-[#A8654A] transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-3.5 h-3.5" />
            Ny samtale
          </button>
        </div>

        {/* Search Bar */}
        <div className="px-3 py-2 border-b border-white/5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#8899A8]" />
            <input
              data-testid="conversation-search"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Søk i samtaler..."
              className="w-full bg-[#1B2740]/50 border border-white/5 rounded-sm pl-9 pr-3 py-2 text-xs text-[#F0F4F8] placeholder-[#8899A8] focus:outline-none focus:border-[#C07850]/30 transition-colors chat-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8899A8] hover:text-[#F0F4F8]"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        {/* Credits Widget */}
        <div className="px-3 py-2 border-b border-white/5">
          <div className="bg-[#1B2740]/60 rounded-lg p-2.5" data-testid="credits-widget">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5 text-[#C07850]" />
                <span className="text-[11px] font-medium text-[#F0F4F8]">
                  {user?.tier === 'enterprise' ? 'Enterprise' : user?.tier === 'premium' ? 'Premium' : 'Gratis'}
                </span>
              </div>
              <span className="text-[10px] text-[#8899A8]" data-testid="credits-remaining">
                {Math.max(0, (user?.credits_total || 70) - (user?.credits_used || 0))} igjen
              </span>
            </div>
            <div className="w-full h-1.5 bg-[#0B1120] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${Math.min(100, Math.round(((user?.credits_used || 0) / (user?.credits_total || 70)) * 100))}%`,
                  backgroundColor: ((user?.credits_used || 0) / (user?.credits_total || 70)) > 0.8 ? '#ef4444' : ((user?.credits_used || 0) / (user?.credits_total || 70)) > 0.5 ? '#eab308' : '#C07850'
                }}
                data-testid="credits-bar"
              />
            </div>
            <p className="text-[9px] text-[#8899A8] mt-1">{user?.credits_used || 0} / {user?.credits_total || 70} brukt</p>
          </div>
        </div>

        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto p-2" data-testid="conversations-list">
          {loadingConvs ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 text-[#8899A8] animate-spin" />
            </div>
          ) : conversations.length === 0 ? (
            <p className="text-[#8899A8] text-xs text-center py-8">
              {searchQuery ? `Ingen treff på "${searchQuery}"` : 'Ingen samtaler ennå'}
            </p>
          ) : (
            conversations.map(conv => (
              <div
                key={conv.id}
                data-testid={`conversation-item-${conv.id}`}
                onClick={() => { loadMessages(conv.id); setSidebarOpen(false); }}
                className={`group flex items-center gap-2 px-3 py-2.5 rounded-sm cursor-pointer mb-0.5 transition-colors ${
                  activeConv?.id === conv.id 
                    ? 'bg-[#1B2740] border border-[#C07850]/20' 
                    : 'hover:bg-[#1B2740]/50 border border-transparent'
                }`}
              >
                <MessageSquare className="w-3.5 h-3.5 text-[#8899A8] flex-shrink-0 avatar" />
                <span className="text-sm text-[#B8C8D8] truncate flex-1">{conv.title}</span>
                <button
                  data-testid={`delete-conversation-${conv.id}`}
                  onClick={(e) => deleteConversation(conv.id, e)}
                  className="opacity-0 group-hover:opacity-100 text-[#8899A8] hover:text-red-400 transition-all"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Sidebar Footer */}
        <div className="p-3 border-t border-white/5">
          {/* Moduler Button */}
          <ModuleTriggerButton 
            onClick={() => {
              setModuleFlyoutOpen(!moduleFlyoutOpen);
              setPrivatFlyoutOpen(false); // Lukk andre flyout
            }} 
            isOpen={moduleFlyoutOpen}
          />
          
          {/* Privat Button */}
          <div className="mt-1">
            <PrivatTriggerButton 
              onClick={() => {
                setPrivatFlyoutOpen(!privatFlyoutOpen);
                setModuleFlyoutOpen(false); // Lukk andre flyout
              }} 
              isOpen={privatFlyoutOpen}
            />
          </div>
          
          {/* Dokumenter */}
          <a
            href="/documents"
            data-testid="nav-documents"
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-[#8899A8] hover:text-[#F0F4F8] hover:bg-[#1B2740]/50 transition-colors text-sm mt-1"
          >
            <FileText className="w-4 h-4" />
            <span className="btn-text">Dokumenter</span>
          </a>
          
          {/* Tidslinje */}
          <a
            href="/timeline"
            data-testid="nav-timeline"
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-[#8899A8] hover:text-[#F0F4F8] hover:bg-[#1B2740]/50 transition-colors text-sm mt-1"
          >
            <Clock className="w-4 h-4" />
            <span className="btn-text">Tidslinje</span>
          </a>
          
          {/* Kjøp moduler - kun for privatbrukere */}
          {user?.account_type !== 'business' && (
            <a
              href="/konto/kjop"
              data-testid="nav-purchase"
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-[#8899A8] hover:text-[#F0F4F8] hover:bg-[#1B2740]/50 transition-colors text-sm mt-1"
            >
              <ShoppingBag className="w-4 h-4" />
              <span className="btn-text">Kjøp moduler</span>
            </a>
          )}
          
          {/* Profil */}
          <a
            href="/profile"
            data-testid="nav-profile"
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-[#8899A8] hover:text-[#F0F4F8] hover:bg-[#1B2740]/50 transition-colors text-sm"
          >
            <User className="w-4 h-4" />
            <span className="btn-text">Min profil</span>
          </a>
          
          {/* Bruker og logout */}
          <div className="flex items-center gap-2 px-3 py-2 mt-2 border-t border-white/5 pt-3">
            <div className="w-7 h-7 bg-[#1B2740] rounded-full flex items-center justify-center">
              <User className="w-3.5 h-3.5 text-[#8899A8]" />
            </div>
            <span className="text-xs text-[#8899A8] truncate flex-1">{user?.name}</span>
            <button
              data-testid="logout-button"
              onClick={logout}
              className="text-[#8899A8] hover:text-red-400 transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="chat-main flex-1 flex flex-col min-w-0">
        {/* Chat Header */}
        <div className="mobile-header h-14 border-b border-white/5 flex items-center px-4 bg-[#131C2E]/40 backdrop-blur-sm flex-shrink-0">
          <button
            data-testid="toggle-sidebar"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="mr-3 text-[#8899A8] hover:text-[#F0F4F8] transition-colors desktop-only"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <h2 className="text-sm font-medium text-[#B8C8D8] truncate header-title" data-testid="active-conversation-title">
            {activeConv?.title || 'RettVerk 1.2.0'}
          </h2>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto chat-messages" data-testid="messages-container">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center p-8 compact-spacing">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <img src="https://customer-assets.emergentagent.com/job_juridisk-motor-no/artifacts/1x4dyzgp_image.jpg" alt="RettVerk" className="w-16 h-16 object-contain" />
              </div>
              {personalizedGreeting?.enabled ? (
                <>
                  <h3 className="text-2xl font-bold mb-3 text-[#F0F4F8]" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="personalized-greeting">
                    {personalizedGreeting.greeting}
                  </h3>
                  {(personalizedGreeting.weather_line || personalizedGreeting.weekday_line || personalizedGreeting.history_line) && (
                    <p className="text-[#8899A8] text-center max-w-md mb-2" data-testid="greeting-context-line">
                      {personalizedGreeting.weather_line || personalizedGreeting.weekday_line}
                    </p>
                  )}
                  {personalizedGreeting.history_line && personalizedGreeting.weather_line && (
                    <p className="text-[#C07850] text-center max-w-md mb-6 text-sm" data-testid="greeting-history-line">
                      {personalizedGreeting.history_line}
                    </p>
                  )}
                  <p className="text-[#8899A8] text-center max-w-md mb-8 leading-relaxed">
                    Hva kan jeg hjelpe deg med?
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-2xl font-bold mb-3 text-[#F0F4F8]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    RettVerk 1.2.0
                  </h3>
                  <p className="text-[#8899A8] text-center max-w-md mb-8 leading-relaxed">
                    Still et juridisk spørsmål om norsk rett. RettVerk 1.2.0 analyserer faktum mot gjeldende lovverk per 2026.
                  </p>
                </>
              )}
              <div className="suggestions-grid grid grid-cols-1 md:grid-cols-2 gap-3 max-w-lg w-full">
                {[
                  'Hva er vilkårene for sykepenger?',
                  'Reglene for oppsigelse i arbeidsforhold',
                  'Rettigheter ved husleietvist',
                  'Krav på spesialundervisning'
                ].map((q, i) => (
                  <button
                    key={i}
                    data-testid={`suggestion-${i}`}
                    onClick={() => { setInput(q); inputRef.current?.focus(); }}
                    className="text-left px-4 py-3 bg-[#131C2E]/60 border border-white/5 rounded-sm text-sm text-[#B8C8D8] hover:border-[#C07850]/30 hover:bg-[#1B2740]/50 transition-all"
                  >
                    <ChevronRight className="w-3 h-3 text-[#C07850] inline mr-1.5" />
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="max-w-4xl mx-auto px-4 py-6 space-y-1">
              {messages.map((msg, idx) => (
                <div
                  key={msg.id || idx}
                  data-testid={`message-${msg.role}-${idx}`}
                  className={`chat-message flex gap-3 py-4 animate-fade-in-up ${msg.role === 'user' ? '' : ''}`}
                  style={{ animationDelay: `${idx * 0.05}s` }}
                >
                  <div className={`avatar w-8 h-8 rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    msg.role === 'user' 
                      ? 'bg-[#1B2740]' 
                      : 'bg-[#C07850]/10 border border-[#C07850]/20'
                  }`}>
                    {msg.role === 'user' 
                      ? <User className="w-4 h-4 text-[#8899A8]" /> 
                      : <img src="https://customer-assets.emergentagent.com/job_juridisk-motor-no/artifacts/1x4dyzgp_image.jpg" alt="RV" className="w-5 h-5 object-contain" />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-[#8899A8] uppercase tracking-wider mb-1 block">
                      {msg.role === 'user' ? 'Du' : 'RettVerk 1.2.0'}
                    </span>
                    {msg.role === 'user' ? (
                      <p className="text-[#F0F4F8] leading-relaxed">{msg.content}</p>
                    ) : (
                      <div className="legal-content">
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                        {msg.isStreaming && (
                          <span className="inline-block w-2 h-5 bg-[#C07850] ml-1 animate-pulse" />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="chat-input-container border-t border-white/5 p-4 bg-[#131C2E]/30 backdrop-blur-sm">
          {uploadedPdf && (
            <div className="max-w-4xl mx-auto mb-2 flex items-center gap-2 bg-[#C07850]/10 border border-[#C07850]/20 rounded-sm px-3 py-2" data-testid="uploaded-pdf-indicator">
              <FileUp className="w-4 h-4 text-[#C07850]" />
              <span className="text-xs text-[#B8C8D8] flex-1 truncate">{uploadedPdf.filename} ({uploadedPdf.page_count} sider)</span>
              <button onClick={() => setUploadedPdf(null)} className="text-[#8899A8] hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
            </div>
          )}
          <form onSubmit={sendMessage} className="max-w-4xl mx-auto flex gap-2" data-testid="chat-input-form">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.docx"
              onChange={handlePdfUpload}
              className="hidden"
              data-testid="pdf-file-input"
            />
            <button
              type="button"
              data-testid="upload-pdf-button"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending || uploadingPdf}
              className="bg-[#131C2E]/80 border border-white/10 rounded-sm px-3 py-3 text-[#8899A8] hover:text-[#C07850] hover:border-[#C07850]/30 transition-colors disabled:opacity-30"
              title="Last opp PDF"
            >
              {uploadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
            </button>
            <input
              ref={inputRef}
              data-testid="chat-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Still et juridisk spørsmål..."
              className="chat-input flex-1 bg-[#131C2E]/80 border border-white/10 rounded-sm px-4 py-3 text-[#F0F4F8] placeholder-[#475569] focus:outline-none focus:border-[#C07850]/50 transition-colors text-sm"
              disabled={sending}
            />
            <button
              data-testid="send-button"
              type="submit"
              disabled={sending || !input.trim()}
              className="bg-[#C07850] text-white px-5 py-3 rounded hover:bg-[#A8654A] transition-colors disabled:opacity-30 flex items-center gap-2"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
          <p className="text-center text-[10px] text-[#475569] mt-2 max-w-4xl mx-auto">
            Juridisk informasjon, ikke advokatråd. Verifiser alltid mot Lovdata.
          </p>
        </div>
      </div>
    </div>
  );
}
